import { Link } from "raviger";
import React from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import {
  AllergyIcon,
  ChillIcon,
  HealthWorkerIcon,
  StethoscopeIcon,
} from "@/CAREUI/icons/CustomIcons";

import {
  KeyboardShortcutBadge,
  ShortcutBadge,
} from "@/Utils/keyboardShortcutComponents";

import { useEncounterShortcutDisplays } from "@/hooks/useEncounterShortcuts";
import { FormDialog } from "./FormsDialog";

export const QuickActions = (props: React.ComponentProps<"div">) => {
  const { t } = useTranslation();
  const getShortcutDisplay = useEncounterShortcutDisplays();

  return (
    <div
      {...props}
      className={cn("grid grid-cols-2 sm:grid-cols-4 gap-3", props.className)}
    >
      <QuickAction
        icon={<AllergyIcon className="text-yellow-700" />}
        title={t("allergy")}
        shortcut={getShortcutDisplay("add-allergy")}
        href={`questionnaire/allergy_intolerance`}
      />
      <QuickAction
        icon={<ChillIcon className="text-pink-700" />}
        title={t("symptoms")}
        shortcut={getShortcutDisplay("add-symptoms")}
        href={`questionnaire/symptom`}
      />
      <QuickAction
        icon={<StethoscopeIcon className="text-blue-800" />}
        title={t("diagnosis")}
        shortcut={getShortcutDisplay("add-diagnosis")}
        href={`questionnaire/diagnosis`}
      />
      <FormDialog
        subjectType="encounter"
        questionnaireTag="encounter_actions"
        trigger={
          <QuickAction
            icon={<HealthWorkerIcon className="text-teal-700" />}
            title={t("forms")}
            shortcut={getShortcutDisplay("add-questionnaire")}
          />
        }
      />
    </div>
  );
};

export function QuickAction({
  icon,
  title,
  shortcut,
  href,
  actionId,
  onClick,
  disabled,
  ...props
}: {
  icon: React.ReactNode;
  title: string;
  shortcut?: string;
  href?: string;
  onClick?: () => void;
  actionId?: string;
  disabled?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const className =
    "flex-1 flex flex-row md:flex-col gap-1.25 p-1 pb-2 rounded-lg shadow bg-white dark:bg-gray-950";

  if (href) {
    return (
      <Link href={href} className={className}>
        <QuickActionContent
          icon={icon}
          title={title}
          shortcut={shortcut}
          actionId={actionId}
        />
      </Link>
    );
  }

  return (
    <button
      className={cn(className, disabled && "opacity-50 cursor-not-allowed")}
      disabled={disabled}
      {...props}
      onClick={onClick}
    >
      <QuickActionContent
        icon={icon}
        title={title}
        shortcut={shortcut}
        actionId={actionId}
      />
    </button>
  );
}

const QuickActionContent = ({
  icon,
  title,
  shortcut,
  actionId,
}: {
  icon: React.ReactNode;
  title: string;
  shortcut?: string;
  actionId?: string;
}) => {
  return (
    <>
      <div className="relative flex md:py-3 py-0 rounded-t-md rounded-b-lg md:bg-gray-100 bg-white dark:bg-gray-950 md:dark:bg-gray-900">
        <KeyboardShortcutBadge shortcut={shortcut} position="top-right" />
        {actionId && <ShortcutBadge actionId={actionId} position="top-right" />}
        <div className="rounded-xl bg-white md:shadow shadow-none mx-auto items-center flex p-2 dark:bg-gray-950">
          {icon}
        </div>
      </div>
      <div className="flex items-center gap-1 justify-center">
        <span className="text-sm font-semibold">{title}</span>
      </div>
    </>
  );
};
